import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Loader2, Receipt } from 'lucide-react'
import StoreHeader from '@/components/store/StoreHeader'
import StoreFooter from '@/components/store/StoreFooter'
import { storeForm } from '@/components/store/storeFormStyles'
import { api, type StorefrontOrderSummary } from '@/lib/api'
import { formatOrderDate, orderStatusBadge } from '@/lib/orders'

/** `/orders`: the signed-in customer's order history, newest first. */
export default function MyOrdersPage() {
  // `undefined` while loading; `null` when signed out (or the session expired).
  const [orders, setOrders] = useState<StorefrontOrderSummary[] | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!localStorage.getItem('srisai_access_token')) {
      setOrders(null)
      return
    }
    let cancelled = false
    api.orders
      .list()
      .then((res) => {
        if (!cancelled) setOrders(res.success && res.data ? res.data : [])
      })
      .catch((e: Error) => {
        if (cancelled) return
        // An expired or invalid session reads as signed out; anything else is a real error.
        if (/token|not found|unauthorized/i.test(e.message)) setOrders(null)
        else {
          setError(e.message)
          setOrders([])
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-orange-50 via-stone-100 to-stone-300/60">
      <StoreHeader />

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 pb-6 pt-3 sm:px-6 sm:pt-4 lg:px-8">
        <Link
          to="/"
          className="mb-2 inline-flex items-center gap-0.5 text-xs font-medium text-stone-600 hover:text-primary-700 sm:text-sm"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          Back to shopping
        </Link>
        <h1 className="text-base font-semibold text-stone-900 sm:text-xl">My orders</h1>

        {error ? <p className={`mt-3 ${storeForm.error}`} role="alert">{error}</p> : null}

        {orders === undefined ? (
          <div className="flex justify-center py-10 text-stone-400" aria-label="Loading orders">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : orders === null ? (
          <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-6 text-center shadow-sm">
            <p className="text-sm text-stone-700">Sign in to see your orders.</p>
            <Link to="/login?next=/orders" className={`mt-4 ${storeForm.submit}`}>
              Sign in
            </Link>
          </div>
        ) : orders.length === 0 ? (
          <div className="mt-4 flex flex-col items-center rounded-2xl border border-stone-200 bg-white p-6 text-center shadow-sm">
            <Receipt className="h-8 w-8 text-stone-300" aria-hidden />
            <p className="mt-2 text-sm text-stone-700">You haven&apos;t placed any orders yet.</p>
            <Link to="/" className="mt-2 text-xs font-semibold text-primary-700 hover:underline sm:text-sm">
              Start shopping
            </Link>
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-stone-100 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
            {orders.map((o) => {
              const badge = orderStatusBadge(o.order_status)
              const more = o.line_count - o.first_items.length
              return (
                <li key={o.order_number}>
                  <Link
                    to={`/order/${o.order_number}`}
                    className="flex items-center gap-3 px-3 py-3 hover:bg-stone-50 sm:px-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-mono text-xs font-semibold text-stone-900 sm:text-sm">{o.order_number}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-xs ${badge.tone}`}>
                          {badge.label}
                        </span>
                      </div>
                      <p className="mt-0.5 text-[11px] text-stone-500 sm:text-xs">
                        {formatOrderDate(o.placed_at)} · {o.item_count} {o.item_count === 1 ? 'item' : 'items'}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-stone-600 sm:text-xs">
                        {o.first_items.join(', ')}
                        {more > 0 ? ` +${more} more` : ''}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-stone-900">${o.total.toFixed(2)}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-stone-400" aria-hidden />
                  </Link>
                </li>
              )
            })}
          </ul>
        )}

        <StoreFooter detailsClassName="mt-6 sm:mt-12" />
      </main>
    </div>
  )
}
